import axios from 'axios';

// Get the API URL from environment or use default
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create a v1-specific axios instance without API key (handled by Netlify Function)
const v1Api = axios.create({
  baseURL: `${apiUrl}/v1`,
  headers: {
    'Content-Type': 'application/json'
  },
});

// Add a request interceptor to include the auth token in requests
v1Api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user && user.token) {
      config.headers.Authorization = `${user.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
v1Api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired, etc.)
    // But don't redirect during login attempts
    if (error.response && error.response.status === 401 && !error.config.url.includes('/admin/login')) {
      // Clear user data and redirect to login
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: async (usernameOrEmail, password) => {
    try {
      console.log('API Service: Preparing login request');
      // Determine if input is email or username
      const isEmail = usernameOrEmail.includes('@');
      const loginData = {
        password
      };
      
      if (isEmail) {
        loginData.email = usernameOrEmail;
      } else {
        loginData.username = usernameOrEmail;
      }
      
      console.log('API Service: Sending login request to /admin/login/');
      const response = await v1Api.post('/admin/login/', loginData);
      console.log('API Service: Login response received', response.status);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('API Service: Login request failed', error.response?.status, error.message);
      // Return error information instead of throwing
      return { 
        success: false, 
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      };
    }
  },
  logout: async () => {
    try {
      await v1Api.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Always clear local storage regardless of API success/failure
    localStorage.removeItem('user');
  },
};

// Version API calls
export const versionAPI = {
  getLatestVersion: async () => {
    const response = await v1Api.get('/levels/version');
    return response.data;
  },
  getVersionPuzzles: async (version) => {
    const response = await v1Api.get(`/levels/${version}/`);
    return response.data;
  },
  createNewVersion: async (boardNumbers) => {
    const response = await v1Api.post('/levels/', { board_numbers: boardNumbers });
    return response.data;
  },
};

// Puzzle API calls
export const puzzleAPI = {
  getAllPuzzles: async () => {
    const response = await v1Api.get('/puzzles/');
    return response.data;
  },
  getPuzzle: async (boardNumber) => {
    const response = await v1Api.get(`/puzzle/${boardNumber}/`);
    return response.data;
  },
  createOrUpdatePuzzle: async (puzzleData, boardNumber = null) => {
    const url = boardNumber ? `/puzzle/${boardNumber}/` : '/puzzle/';
    const response = await v1Api.post(url, puzzleData);
    return response.data;
  },
  solvePuzzle: async (pattern) => {
    const response = await v1Api.post('/solve/puzzle/', { pattern });
    return response.data;
  }
};

// Daily Challenge API calls
export const dailyChallengeAPI = {
  getDailyChallengesRange: async (startDate, endDate) => {
    const response = await v1Api.get('/daily-challenges/range/', {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });
    return response.data;
  },
  setDailyChallenge: async (date, boardNumber) => {
    const response = await v1Api.post(`/puzzles/daily/${date}/`, {
      board_number: boardNumber
    });
    return response.data;
  },
  removeDailyChallenge: async (date) => {
    const response = await v1Api.delete(`/puzzles/daily/${date}/`);
    return response.data;
  }
};

// Export the v1Api instance for direct use if needed
export { v1Api };

// Export the v1Api instance as default
export default v1Api;
